import { Alert, FormControl, Grid, InputLabel, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import ReactJson from "react-json-view";
import { useLocalState } from "../../utils/hooks/global/useLocalStorage";
import CustomButton from "../global/CustomButton";
import { Loading } from "../global/Loading";
import ApiResponseExplorer from "./ApiResponseExplorer";
import JsonSourceFinder from "./JsonSourceFinder";

const templateAPIScript = {
  type: "API",
  url: "",
  script: {
    structure: {
      apiStructureDeposit: "[]",
      apiStructureWithdraw: "[]",
    },
  },
  true: true,
};

const templateSCRAPEScript = {
  type: "SCRAPE",
  url: "",
  actions: [
    { action: "waitForSelector", selector: "tr:has(td img[alt='UST'])" },
    {
      action: "extract",
      depositStatusSelector:
        "tr:has(td img[alt='UST']) td:nth-child(2) div.status div:nth-child(1) span",
      withdrawalStatusSelector:
        "tr:has(td img[alt='UST']) td:nth-child(2) div.status div:nth-child(2) span",
    },
  ],
  true: "Active",
};

const EditExchangeProfileForm = ({ entity, onSubmit, mode }) => {
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [testResults, setTestResults] = useState();
  const [pasteMode, setPasteMode] = useState(false);
  const [jsonText, setJsonText] = useState("");
  const [jsonError, setJsonError] = useState("");
  // The scrape config replaced by "Use as API check", for Undo
  const [replacedScrapeScript, setReplacedScrapeScript] = useState();
  const [jwt] = useLocalState(null, "jwt");
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const [formData, setFormData] = useState({
    exchange: {
      exchangeStatus: {
        failScoreThreshold: entity.exchangeStatus?.failScoreThreshold ?? 5.0,
        statusScript:
          entity.exchangeStatus?.statusScript ?? templateSCRAPEScript,
      },
    },
  });

  // In paste mode the text box is the config: Test and Save use it directly,
  // so what is shown is what gets tested and saved.
  function resolveStatusScript() {
    if (!pasteMode) return formData.exchange.exchangeStatus.statusScript;
    try {
      const parsed = JSON.parse(jsonText);
      setJsonError("");
      return parsed;
    } catch (e) {
      setJsonError("Invalid JSON: " + e.message);
      return null;
    }
  }

  async function testScript() {
    const statusScript = resolveStatusScript();
    if (!statusScript) return;
    setLoading(true);
    fetch(`${backendUrl}/exchanges/testScript/`, {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: "Bearer " + jwt,
      },
      body: JSON.stringify({ statusScript }),
    })
      .then((response) => {
        if (!response.ok) {
          if (response.status === 409) {
            throw new Error("There was an error processing your request.");
          }
        }
        return response.json();
      })
      .then((data) => {
        setErrorMessage("");
        setTestResults(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("There was an error!", error.message);
        setErrorMessage(error.message);
        setLoading(false);
      });
  }

  // The config as currently shown, without flagging half-typed JSON
  const currentScript = (() => {
    if (!pasteMode) return formData.exchange.exchangeStatus.statusScript;
    try {
      return JSON.parse(jsonText);
    } catch {
      return null;
    }
  })();

  // Changes the config in whichever editor is showing
  function updateStatusScript(change) {
    setTestResults(undefined);
    if (pasteMode) {
      const parsed = resolveStatusScript();
      if (parsed) setJsonText(JSON.stringify(change(parsed), null, 2));
      return;
    }
    setFormData((prevData) => ({
      exchange: {
        ...prevData.exchange,
        exchangeStatus: {
          ...prevData.exchange.exchangeStatus,
          statusScript: change(prevData.exchange.exchangeStatus.statusScript),
        },
      },
    }));
  }

  const upValues = Array.isArray(currentScript?.upValues) &&
    currentScript.upValues.length
    ? currentScript.upValues
    : [currentScript?.true];

  function applyExpression(type, expression) {
    const key =
      type === "deposit" ? "apiStructureDeposit" : "apiStructureWithdraw";
    updateStatusScript((script) => ({
      ...script,
      script: {
        ...script.script,
        structure: { ...script.script?.structure, [key]: expression },
      },
    }));
  }

  // Adds to upValues when the config has them (they replace "true")
  function applyUpValue(value) {
    updateStatusScript((script) =>
      Array.isArray(script.upValues) && script.upValues.length
        ? { ...script, upValues: [...script.upValues, value] }
        : { ...script, true: value }
    );
  }

  function switchToJsonSource(url) {
    setReplacedScrapeScript(currentScript);
    updateStatusScript(() => ({
      type: "API",
      url,
      script: {
        structure: { apiStructureDeposit: "", apiStructureWithdraw: "" },
      },
      true: true,
    }));
  }

  function undoJsonSource() {
    const scrapeScript = replacedScrapeScript;
    setReplacedScrapeScript(undefined);
    updateStatusScript(() => scrapeScript);
  }

  function enterPasteMode() {
    setJsonText(
      JSON.stringify(
        formData.exchange.exchangeStatus.statusScript ?? templateSCRAPEScript,
        null,
        2
      )
    );
    setJsonError("");
    setPasteMode(true);
  }

  function applyJson() {
    let parsed;
    try {
      parsed = JSON.parse(jsonText);
    } catch (e) {
      setJsonError("Invalid JSON: " + e.message);
      return;
    }
    setJsonError("");
    setFormData((prevData) => {
      return {
        exchange: {
          ...prevData.exchange,
          exchangeStatus: {
            ...prevData.exchange.exchangeStatus,
            statusScript: parsed,
          },
        },
      };
    });
    setPasteMode(false);
  }

  function handleAddForm(src) {
    if (src.name === "actions") {
      const actions = src.existing_value;
      const action = {
        action: "waitForSelector",
        selector: "",
      };
      actions.push(action);
      setFormData((prevData) => {
        return {
          exchange: {
            ...prevData.exchange,
            exchangeStatus: {
              ...prevData.exchange.exchangeStatus,
              statusScript: {
                ...prevData.exchange.exchangeStatus.statusScript,
                actions: actions,
              },
            },
          },
        };
      });
    }
  }

  function handleChangeForm(src) {
    if (src.name === "type" && src.existing_value != src.new_value) {
      switch (src.new_value?.toUpperCase()) {
        case "API":
          setFormData((prevData) => {
            return {
              exchange: {
                ...prevData.exchange,
                exchangeStatus: {
                  ...prevData.exchange.exchangeStatus,
                  statusScript: templateAPIScript,
                },
              },
            };
          });
          break;
        case "SCRAPE":
          setFormData((prevData) => {
            return {
              exchange: {
                ...prevData.exchange,
                exchangeStatus: {
                  ...prevData.exchange.exchangeStatus,
                  statusScript: templateSCRAPEScript,
                },
              },
            };
          });
          break;
        default:
          break;
      }
    } else if (src.name === "action" && src.existing_value != src.new_value) {
      const newSrc = src.updated_src.actions;
      let action = {};
      switch (src.new_value?.toLowerCase()) {
        case "waitforselector":
          action = {
            action: "waitForSelector",
            selector: "",
          };
          break;
        case "waitandclick":
          action = {
            action: "waitAndClick",
            selector: "",
          };
          break;
        case "click":
          action = {
            action: "click",
            selector: "",
          };
          break;
        case "waitfortimeout":
          action = {
            action: "waitForTimeout",
            time: 1000,
          };
          break;
        case "extract":
          action = {
            action: "extract",
            depositStatusSelector: "",
            withdrawalStatusSelector: "",
          };
          break;
        default:
          action = {
            action: "waitForSelector",
            selector: "",
          };
          break;
      }
      newSrc[src.namespace[1]] = action;
      setFormData((prevData) => {
        return {
          exchange: {
            ...prevData.exchange,
            exchangeStatus: {
              ...prevData.exchange.exchangeStatus,
              statusScript: {
                ...prevData.exchange.exchangeStatus.statusScript,
                actions: newSrc,
              },
            },
          },
        };
      });
    } else if (src.name === "index" && src.new_value != undefined) {
      const newIndex = src.new_value;
      const oldIndex = src.namespace[1];
      const newActions = [...src.updated_src.actions];

      [newActions[oldIndex], newActions[newIndex]] = [
        newActions[newIndex],
        newActions[oldIndex],
      ];

      setFormData((prevData) => {
        return {
          exchange: {
            ...prevData.exchange,
            exchangeStatus: {
              ...prevData.exchange.exchangeStatus,
              statusScript: {
                ...prevData.exchange.exchangeStatus.statusScript,
                actions: newActions,
              },
            },
          },
        };
      });
    } else {
      setFormData((prevData) => {
        return {
          exchange: {
            ...prevData.exchange,
            exchangeStatus: {
              ...prevData.exchange.exchangeStatus,
              statusScript: {
                ...src.updated_src,
              },
            },
          },
        };
      });
    }
  }

  const handleInputChange = (e) => {
    setErrorMessage("");
    const { name, value } = e.target;
    setFormData((prevData) => {
      return {
        exchange: {
          ...prevData.exchange,
          exchangeStatus: {
            ...prevData.exchange.exchangeStatus,
            [name]: value,
          },
        },
      };
    });
  };

  const handleSubmit = () => {
    const statusScript = resolveStatusScript();
    if (!statusScript) return;
    const payload = {
      ...formData,
      exchange: {
        ...formData.exchange,
        exchangeStatus: { ...formData.exchange.exchangeStatus, statusScript },
      },
    };
    fetch(`${backendUrl}/entities/${entity.baseEntity.id}`, {
      method: "PUT",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: "Bearer " + jwt,
      },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (!response.ok) {
          if (response.status === 409) {
            throw new Error("There was an error processing your request.");
          }
        }
        return response.json();
      })
      .then((data) => {
        setErrorMessage("");
        onSubmit();
      })
      .catch((error) => {
        console.error("There was an error!", error.message);
        setErrorMessage(error.message);
      });
  };

  return (
    <Grid container gap={2}>
      <Grid item xs={12}>
        {" "}
        <InputLabel
          id="has-native-assets-label"
          sx={{
            fontSize: "1rem",
            fontWeight: "bold",
          }}
        >
          Fail Score Threshold
        </InputLabel>
      </Grid>
      <Grid item xs={12}>
        <FormControl>
          <TextField
            id="failScoreThreshold"
            name="failScoreThreshold"
            variant="outlined"
            inputProps={{
              step: ".1",
            }}
            fullWidth
            sx={{ flex: "0.8" }}
            type="number"
            value={formData.exchange.exchangeStatus.failScoreThreshold}
            onChange={handleInputChange}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        {" "}
        <InputLabel
          id="has-native-assets-label"
          sx={{
            fontSize: "1rem",
            fontWeight: "bold",
          }}
        >
          Deposits/Withdrawals Script
        </InputLabel>
      </Grid>
      <Grid item xs={12} display={"flex"} gap={"10px"}>
        <CustomButton
          buttonText={pasteMode ? "Use Builder" : "Paste JSON"}
          color={"blackOutlined"}
          sx={{ width: "140px" }}
          onClick={() => (pasteMode ? setPasteMode(false) : enterPasteMode())}
        />
        {pasteMode && (
          <CustomButton
            buttonText={"Apply JSON"}
            color={"black"}
            sx={{ width: "140px" }}
            onClick={applyJson}
          />
        )}
      </Grid>
      {pasteMode ? (
        <Grid item xs={12}>
          <TextField
            multiline
            minRows={6}
            maxRows={14}
            fullWidth
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            error={!!jsonError}
            helperText={jsonError}
            InputProps={{
              sx: {
                fontFamily: "monospace",
                fontSize: ".85rem",
                alignItems: "flex-start",
                maxHeight: "40vh",
                overflow: "auto",
              },
            }}
          />
        </Grid>
      ) : (
        <Grid item xs={12}>
          <ReactJson
            src={
              formData.exchange.exchangeStatus.statusScript ?? templateAPIScript
            }
            style={{
              border: "1px solid #c2c2c2",
              borderRadius: "4px",
              padding: "10px",
              fontSize: ".85rem",
              fontWeight: "400",
              maxHeight: "40vh",
              overflow: "scroll",
            }}
            name={null}
            iconStyle="square"
            displayDataTypes={false}
            displayObjectSize={false}
            enableClipboard={true}
            onAdd={handleAddForm}
            onEdit={handleChangeForm}
            onDelete={handleChangeForm}
          />
        </Grid>
      )}
      {currentScript?.type === "SCRAPE" && (
        <Grid item xs={12}>
          <JsonSourceFinder
            script={currentScript}
            onUseAsApi={switchToJsonSource}
          />
        </Grid>
      )}
      {currentScript?.type === "API" && replacedScrapeScript && (
        <Grid item xs={12}>
          <Alert
            severity="info"
            action={
              <CustomButton
                buttonText="Undo"
                color="blackOutlined"
                onClick={undoJsonSource}
              />
            }
          >
            Switched to an API check. Pick the deposit and withdrawal fields
            below, Test, then Save. Nothing is saved until you click Save.
          </Alert>
        </Grid>
      )}
      {currentScript?.type === "API" && (
        <Grid item xs={12}>
          <ApiResponseExplorer
            autoFetch={!!replacedScrapeScript}
            url={currentScript.url}
            deposit={currentScript.script?.structure?.apiStructureDeposit}
            withdrawal={currentScript.script?.structure?.apiStructureWithdraw}
            upValues={upValues}
            onUse={applyExpression}
            onUseUpValue={applyUpValue}
          />
        </Grid>
      )}
      {loading ? (
        <Grid item xs={12} textAlign={"center"}>
          <Loading />
        </Grid>
      ) : (
        <>
          {testResults && (
            <>
              <Grid item xs={12}>
                <Alert
                  severity={
                    testResults.errorMessage ||
                    testResults.depositStatus === null ||
                    testResults.withdrawalStatus === null
                      ? "error"
                      : testResults.depositStatus && testResults.withdrawalStatus
                      ? "success"
                      : "warning"
                  }
                >
                  {testResults.errorMessage
                    ? `Check failed: ${testResults.errorMessage}`
                    : testResults.depositStatus === null ||
                      testResults.withdrawalStatus === null
                    ? `Check failed: could not read ${[
                        testResults.depositStatus === null && "deposit",
                        testResults.withdrawalStatus === null && "withdrawal",
                      ]
                        .filter(Boolean)
                        .join(" and ")} status`
                    : `Deposit: ${
                        testResults.depositStatus ? "up" : "down"
                      } · Withdrawal: ${
                        testResults.withdrawalStatus ? "up" : "down"
                      }`}
                </Alert>
              </Grid>
              <Grid item xs={12}>
                {" "}
                <InputLabel
                  id="has-native-assets-label"
                  sx={{
                    fontSize: "1rem",
                    fontWeight: "bold",
                  }}
                >
                  Response
                </InputLabel>
              </Grid>
              <Grid item xs={12}>
                <ReactJson
                  src={testResults}
                  style={{
                    border: "1px solid #c2c2c2",
                    borderRadius: "4px",
                    padding: "10px",
                    fontSize: ".85rem",
                    fontWeight: "400",
                    maxHeight: "15vh",
                    overflow: "scroll",
                  }}
                  name={null}
                  iconStyle="square"
                  displayDataTypes={false}
                  displayObjectSize={false}
                  enableClipboard={true}
                />
              </Grid>
            </>
          )}
        </>
      )}

      <Grid
        item
        xs={12}
        textAlign={"center"}
        display={"flex"}
        justifyContent={"center"}
        gap={"10px"}
      >
        <CustomButton
          buttonText={"Test"}
          color={"blackOutlined"}
          sx={{
            width: "80px",
          }}
          onClick={testScript}
        />
        <CustomButton
          buttonText={"Save"}
          color={"black"}
          sx={{
            width: "80px",
          }}
          onClick={handleSubmit}
        />
      </Grid>
    </Grid>
  );
};

export default EditExchangeProfileForm;
